interface Judge {
  name?: string;
  designation?: string;
}

interface RoomInfo {
  benchName?: string;
  courtRoom?: string;
  highcourt?: string;
  judges?: Judge[];
}

interface CauseInfo {
  cnr?: string;
  petitioner?: string;
  respondent?: string;
}

type StreamStatus =
  | "waiting"
  | "scheduled"
  | "live"
  | "paused"
  | "rec"
  | "ended";

interface Props {
  roomInfo?: RoomInfo | null;
  activeCause?: CauseInfo | null;
  prevCause?: CauseInfo | null;
  nextCause?: CauseInfo | null;
  courtRoomLabel?: string;
  displayStatus: StreamStatus;
}

export default function PublicStreamHeader({
  roomInfo,
  activeCause,
  prevCause,
  nextCause,
  courtRoomLabel,
}: Props) {
  const marqueeText = [
    roomInfo?.highcourt ? `${roomInfo.highcourt} High Court` : undefined,
    roomInfo?.benchName,
    roomInfo?.judges?.length
      ? roomInfo.judges
          .map(
            (j) =>
              `${j.designation ? `${j.designation} ` : "Hon'ble "}${j.name}`,
          )
          .join(" | ")
      : undefined,
    activeCause?.petitioner && activeCause?.respondent
      ? `${activeCause.petitioner} vs ${activeCause.respondent}`
      : undefined,
  ]
    .filter(Boolean)
    .join(" • ");

  const showCnrRow = activeCause?.cnr || prevCause?.cnr || nextCause?.cnr;

  return (
    <header
      className="w-full shrink-0 border-b-2 border-indigo-400/60 shadow-lg z-30 bg-gradient-to-br
     from-blue-800 via-blue-900 to-slate-950"
    >
      <div className="w-full px-3 sm:px-6 py-2 flex flex-col items-center text-center gap-3">
        {/* Court Room */}
        {(roomInfo?.courtRoom || courtRoomLabel) && (
          <div className="font-extrabold text-white text-2xl sm:text-4xl lg:text-4xl leading-tight">
            {`${roomInfo?.courtRoom} . ${courtRoomLabel}`}
          </div>
        )}

        {showCnrRow && (
          <div className="w-full flex items-stretch justify-center gap-2 sm:gap-6">
            {prevCause?.cnr && (
              <div className="flex-none w-28 sm:w-44 lg:w-56 flex flex-col items-center justify-center min-w-0 rounded-xl border-2 border-white/25 bg-white/10">
                <span className="text-blue-200 text-[10px] sm:text-xs uppercase tracking-widest font-semibold">
                  Previous
                </span>
                <span className="text-white/80 text-lg sm:text-2xl lg:text-3xl font-bold truncate max-w-full">
                  {prevCause.cnr}
                </span>
              </div>
            )}

            {activeCause?.cnr && (
              <div className="flex-none w-40 sm:w-64 lg:w-80 flex flex-col items-center justify-center min-w-0 rounded-xl border-2 border-indigo-300/60 bg-indigo-500/25">
                <span className="text-blue-100 text-xs sm:text-sm uppercase tracking-widest font-semibold">
                  Current Case
                </span>
                <span className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-none truncate max-w-full cnr-text-blink-strong">
                  {activeCause.cnr}
                </span>
              </div>
            )}

            {nextCause?.cnr && (
              <div className="flex-none w-28 sm:w-44 lg:w-56 flex flex-col items-center justify-center min-w-0 rounded-xl border-2 border-white/25 bg-white/10">
                <span className="text-blue-200 text-[10px] sm:text-xs uppercase tracking-widest font-semibold">
                  Next
                </span>
                <span className="text-white/80 text-lg sm:text-2xl lg:text-3xl font-bold truncate max-w-full">
                  {nextCause.cnr}
                </span>
              </div>
            )}
          </div>
        )}

        {marqueeText && (
          <div className="w-full overflow-hidden border-t border-white/20 pt-2">
            <div className="flex animate-marquee-track whitespace-nowrap">
              <span className="px-8 text-blue-100 text-sm sm:text-lg font-medium">
                {marqueeText}
              </span>
              <span className="px-8 text-blue-100 text-sm sm:text-lg font-medium">
                {marqueeText}
              </span>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
